import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useAlert } from "@/hooks/useAlert";
import { useLocalStorageFlag } from "@/hooks/useLocalStorageFlag";
import { useGetSim } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";

/** Poll interval used while an order is still being provisioned. */
const PENDING_REFETCH_INTERVAL_MS = 10000;

/**
 * Loads the SIM currently addressed by the `simId` query param for the device
 * management view. Beyond the plain query it:
 * - polls every 10s while the SIM has a pending service, so the UI follows the
 *   provisioning through to completion,
 * - raises a throttled info alert when the SIM cannot be loaded, and
 * - bounces the user back to the overview when the backend reports the SIM no
 *   longer exists.
 *
 * Returns the underlying react-query result verbatim.
 */
export const useManagedSim = (simId: string) => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { t } = useTranslation("portal");
  const {
    shouldShowError: shouldShowSimDataError,
    setErrorFired: setSimDataErrorFired,
  } = useLocalStorageFlag("simDataErrorFiredAt");

  const [isPendingSim, setIsPendingSim] = useState(false);
  const refetchInterval = isPendingSim && PENDING_REFETCH_INTERVAL_MS;

  return useGetSim(
    { simId: parseInt(simId) },
    {
      query: {
        enabled: !isNaN(parseInt(simId)),
        refetchInterval,
        onSuccess: (data) => setIsPendingSim(!!data.servicePending),
        onError: (error) => {
          if (shouldShowSimDataError()) {
            showAlert({
              type: AlertTypes.info,
              text: t("Error.eSimError"),
            });
            setSimDataErrorFired();
          }
          if (
            typeof error?.response?.data === "string" &&
            error?.response?.data.includes("no sim found for sim identifier")
          ) {
            navigate("..", { replace: true });
          }
        },
        staleTime: 15 * 1000,
        refetchOnMount: true,
      },
    },
  );
};
